import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    HStack,
    Icon,
    SimpleGrid,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    useColorModeValue,
    useDisclosure,
    useToast,
    VStack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    FiAward,
    FiBell,
    FiCalendar,
    FiMapPin,
    FiRepeat,
    FiSettings,
    FiUsers,
} from 'react-icons/fi';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate, TranslationKeys } from '../../../i18n';
import {
    MAX_RECURRING_HORIZON_DAYS,
    MAX_RECURRING_OCCURRENCES,
    RecurrenceErrorCode,
    countRecurringOccurrences,
    validateRecurrence,
} from '../utils/recurrence-validation';
import { SportType } from '../../../models/court/dto/court.dto';
import { CreateEventInputDto } from '../../../models/event/dto/create-event.dto';
import {
    EventDto,
    EventType,
    GenderRestriction,
    TournamentCategory,
} from '../../../models/event/dto/event.dto';
import { UpdateEventInputDto } from '../../../models/event/dto/update-event.dto';
import { OrganizationLocationDto } from '../../../models/location/dto/location.dto';
import { MAX_LEVEL, MIN_LEVEL } from '../../../models/user/dto/user.dto';
import { headingFont, spacing, typography } from '../../../theme/tokens';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useListCoachs } from '../../coach-management/hooks/useListCoachs';
import { useGetCourts } from '../../court-management/hooks/useGetCourts';
import LocationPicker from '../../location-management/components/LocationPicker';
import { useSingleClubAutoSelect } from '../../location-management/hooks/useSingleClubAutoSelect';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetCohorts } from '../../user-cohort/hooks/useGetCohorts';
import { useCreateEvent } from '../hooks/useCreateEvent';
import { useUpdateEvent } from '../hooks/useUpdateEvent';
import { buildSportOptions } from '../utils/sport-options';

// ─── Constants ───────────────────────────────────────────────────────────────

const RANKING_OPTIONS = [
    { label: 'P25', value: 'P25' },
    { label: 'P50', value: 'P50' },
    { label: 'P100', value: 'P100' },
    { label: 'P200', value: 'P200' },
    { label: 'P500', value: 'P500' },
    { label: 'P1000', value: 'P1000' },
    { label: 'P1500', value: 'P1500' },
    { label: 'P2000', value: 'P2000' },
];

const EVENT_TYPE_OPTIONS = [
    { label: 'event.type.class', value: EventType.CLASS },
    { label: 'event.type.competition', value: EventType.COMPETITION },
    { label: 'event.type.official_competition', value: EventType.OFFICIAL_COMPETITION },
    { label: 'event.type.other', value: EventType.OTHER },
];


const DAYS_OF_WEEK = [
    { value: 0, key: 'sun' },
    { value: 1, key: 'mon' },
    { value: 2, key: 'tue' },
    { value: 3, key: 'wed' },
    { value: 4, key: 'thu' },
    { value: 5, key: 'fri' },
    { value: 6, key: 'sat' },
];

/**
 * Typed as `TranslationKeys` so the compiler proves every message exists, and
 * keyed by the full `RecurrenceErrorCode` union so adding a rejection reason to
 * the validator fails the build until it has copy in both languages.
 */
const RECURRENCE_ERROR_KEYS: Record<RecurrenceErrorCode, TranslationKeys> = {
    missing_start_time: 'event.recurrence_error.missing_start_time',
    no_days_selected: 'event.recurrence_error.no_days_selected',
    invalid_day: 'event.recurrence_error.invalid_day',
    missing_end_date: 'event.recurrence_error.missing_end_date',
    invalid_end_date: 'event.recurrence_error.invalid_end_date',
    end_date_before_start: 'event.recurrence_error.end_date_before_start',
    horizon_too_long: 'event.recurrence_error.horizon_too_long',
    no_occurrences: 'event.recurrence_error.no_occurrences',
    too_many_occurrences: 'event.recurrence_error.too_many_occurrences',
};

/** Which control a rejection reason belongs under. */
const DAY_ERROR_CODES: RecurrenceErrorCode[] = [
    'no_days_selected',
    'invalid_day',
];

const GENDER_OPTIONS = [
    { label: 'event.men', value: GenderRestriction.MEN },
    { label: 'event.women', value: GenderRestriction.WOMEN },
    { label: 'event.no_restriction', value: GenderRestriction.NO_RESTRICTION },
];

// ─── Types ───────────────────────────────────────────────────────────────────

type CreateEventFormType = Omit<CreateEventInputDto, 'locationId'> & {
    organizationLocation?: OrganizationLocationDto;
};

const defaultValues: CreateEventFormType = {
    name: '',
    location: '',
    capacity: 0,
    startTime: '',
    endTime: '',
    eventType: EventType.CLASS,
    notificationTitle: '',
    sportType: SportType.PADEL,
    price: 0,
    teamSize: undefined,
    courtIds: [],
    isRecurring: false,
    recurringDaysOfWeek: [],
    recurringEndDate: '',
    cohortId: undefined,
    coachId: undefined,
    requiresLicenseNumber: false,
};

// ─── Animation variants ─────────────────────────────────────────────────────

const MotionBox = motion(Box);

const sectionReveal = {
    hidden: { opacity: 0, height: 0, overflow: 'hidden' as const },
    visible: {
        opacity: 1,
        height: 'auto',
        overflow: 'visible' as const,
        transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
    },
    exit: {
        opacity: 0,
        height: 0,
        overflow: 'hidden' as const,
        transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
    },
};

// ─── Section Header ─────────────────────────────────────────────────────────

function SectionHeader({
    icon,
    label,
}: {
    icon: React.ElementType;
    label: string;
}) {
    const labelColor = useColorModeValue('secondaryGray.600', 'gray.400');
    const iconColor = useColorModeValue('brand.500', 'brand.400');
    const dividerColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    return (
        <Flex align="center" gap="10px" mb="16px" mt="8px">
            <Icon as={icon} color={iconColor} boxSize="16px" />
            <Text
                {...typography.formSectionLabel}
                color={labelColor}
                fontFamily={headingFont}
            >
                {label}
            </Text>
            <Box flex={1} h="1px" bg={dividerColor} />
        </Flex>
    );
}

// ─── Date Picker Field ──────────────────────────────────────────────────────

function DatePickerField({
    label,
    selected,
    onChange,
    minDate,
    isInvalid,
    placeholder,
}: {
    label: string;
    selected: Date | null;
    onChange: (date: Date | null) => void;
    minDate?: Date;
    isInvalid?: boolean;
    placeholder?: string;
}) {
    const labelColor = useColorModeValue('secondaryGray.900', 'white');

    return (
        <FormControl isInvalid={isInvalid}>
            <FormLabel {...typography.formLabel} color={labelColor} mb="6px">
                {label}
            </FormLabel>
            <DatePicker
                selected={selected}
                onChange={onChange}
                showTimeSelect
                timeIntervals={15}
                dateFormat="Pp"
                timeCaption={translate('event.time')}
                placeholderText={placeholder}
                wrapperClassName="date-picker-wrapper"
                className="date-picker-input"
                minDate={minDate}
                isClearable
                popperProps={{ strategy: 'fixed' }}
            />
            {isInvalid && (
                <FormErrorMessage mt="4px">
                    {translate('form.must_be_valid_date')}
                </FormErrorMessage>
            )}
        </FormControl>
    );
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface CreateEventFormProps {
    event?: EventDto;
    isOpen?: boolean;
    onClose?: () => void;
    initialStartTime?: string;
    initialEndTime?: string;
    initialCourtId?: number;
    onEventCreated?: () => void;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function CreateEventForm({
    event,
    isOpen: externalIsOpen,
    onClose: externalOnClose,
    initialStartTime,
    initialEndTime,
    initialCourtId,
    onEventCreated,
}: CreateEventFormProps = {}) {
    const { mutateAsync: createEvent, isLoading: isCreating } = useCreateEvent();
    const { mutateAsync: updateEvent, isLoading: isUpdating } = useUpdateEvent();
    const [formData, setFormData] = useState<CreateEventFormType>(defaultValues);
    const [levelRequired, setLevelRequired] = useState(false);
    const [rankingRequired, setRankingRequired] = useState(false);
    const [isRecurring, setIsRecurring] = useState(false);
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    // R94-1 took `isError` as well as `data`, which was the right half and only
    // half. Run 130 measured the other one by finally driving this form from
    // the courts suite the shared file had been CLAIMING to cover for eleven
    // runs (R119-5): `isCourtsError ? <error> : courts?.length ? <list> : …`
    // tests the flag ALONE, so a failed BACKGROUND refetch — the ones
    // react-query fires on mount, focus and reconnect without the operator
    // asking, as `queryClient.ts` says in as many words — replaced a populated
    // court picker with an error line, mid-form, discarding the operator's
    // context because a refresh they did not request had failed. That is the
    // R96-3 mirror defect, and it is exactly what `classifyRead`'s
    // `unreadable` arm exists to prevent: it keeps a good value on screen and
    // reports only a refusal with nothing behind it.
    //
    // Both reads now go through the shared classifier rather than a hand-rolled
    // ternary, which is also what puts them on the same footing as the coach
    // read 400 lines below (run 119). Both gates are now real: run 134 gave
    // `useGetCourts` the `enabled: Boolean(locationId)` it had never had
    // (R110-6), so its `never-issued` state is reachable here and the arm below
    // renders it from `courtsReadState` instead of re-deriving the gate from
    // `formData.organizationLocation` — D62, and the two can no longer drift.
    const {
        data: courts,
        isError: isCourtsError,
        isInitialLoading: isCourtsLoading,
    } = useGetCourts(formData.organizationLocation?.id || '');
    const courtsReadState = classifyRead(isCourtsError, isCourtsLoading, courts);
    // The sharpest case for the cohorts read is EDIT, not create: `cohortId` is
    // seeded from `event.cohort?.id`, so a read that answered nothing leaves the
    // select with no option matching that value and it renders as `event.none` —
    // an event that IS restricted to a cohort, displayed as open to everyone, on
    // the form an operator edits it from.
    const {
        data: cohorts,
        isError: isCohortsError,
        isInitialLoading: isCohortsLoading,
    } = useGetCohorts(selectedOrganization?.id);
    const cohortsReadState = classifyRead(isCohortsError, isCohortsLoading, cohorts);
    // R119-3 — this read was the one of the three on this form that took NO
    // error flag, and it stayed that way because of the two above it. The
    // `hook-family-census-gate` span matcher was bounded by a character count
    // (200) rather than by the statement, so it reached back over the courts
    // and cohorts declarations, found THEIR `isError`, and reported this site
    // as handled: the more of the file run 94 fixed, the more correct
    // neighbours there were to borrow from (R94-2 / D59, measured run 119).
    //
    // The defect it hid is the R89-4 shape on an optional control: `coachs`
    // undefined falls through to `coachOptions = [none]`, so an operator
    // creating an event is shown a coach picker containing nobody — a
    // definitive claim that this organization has no staff, made from a read
    // that failed or was never sent.
    const {
        data: coachs,
        isError: isCoachsError,
        isInitialLoading: isCoachsLoading,
    } = useListCoachs(selectedOrganization?.id || '');
    const coachsReadState = classifyRead(isCoachsError, isCoachsLoading, coachs);
    const toast = useToast();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const modalIsOpen =
        externalIsOpen !== undefined ? externalIsOpen : isOpen;
    const modalOnClose = externalOnClose || onClose;

    // ─── Derived state ──────────────────────────────────────────────────

    const isCompetition =
        formData.eventType === EventType.COMPETITION ||
        formData.eventType === EventType.OFFICIAL_COMPETITION;
    const isOfficialCompetition =
        formData.eventType === EventType.OFFICIAL_COMPETITION;

    // ─── Effects ────────────────────────────────────────────────────────

    useEffect(() => {
        if (!event) {
            handleInputChange('organizationLocation', selectedLocation);
        }
    }, [selectedLocation, event]);

    useEffect(() => {
        if (initialStartTime && initialEndTime && !event) {
            setFormData((prev) => ({
                ...prev,
                startTime: initialStartTime,
                endTime: initialEndTime,
                courtIds: initialCourtId ? [initialCourtId] : [],
            }));
        }
    }, [initialStartTime, initialEndTime, initialCourtId, event]);

    useEffect(() => {
        if (event) {
            const endTime = new Date(
                new Date(event.startTime).getTime() + event.duration
            );
            setFormData({
                name: event.name,
                location: event.location,
                capacity: event.capacity,
                startTime: toLocalDateTimeString(event.startTime),
                endTime: toLocalDateTimeString(endTime.toISOString()),
                eventType: event.eventType,
                notificationTitle: '',
                sportType: event.sportType || SportType.PADEL,
                prizes: event.prizes,
                minLevel: event.minLevel,
                maxLevel: event.maxLevel,
                minRanking: event.minRanking,
                maxRanking: event.maxRanking,
                price: event.price || 0,
                organizationLocation: event.organizationLocation,
                courtIds: event.courts?.map((c) => c.id) || [],
                tournamentCategory: event.tournamentCategory,
                genderRestriction: event.genderRestriction,
                requiresLicenseNumber: event.requiresLicenseNumber,
                cohortId: event.cohort?.id,
                coachId: event.coach?.id,
                teamSize: event.teamSize ?? undefined,
            });
            setLevelRequired(!!(event.minLevel || event.maxLevel));
            setRankingRequired(!!(event.minRanking || event.maxRanking));
        }
        // `modalIsOpen` is a dependency so re-opening the SAME event re-hydrates
        // from the server row. `closeModal` only resets `formData` for a NEW
        // event, so without this an edit abandoned without saving stayed in
        // state and was silently re-submitted on the next save. Harmless for
        // most fields; not for this one — a stray "requires licence" tick starts
        // 409-ing every un-licensed entrant.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [event, modalIsOpen]);

    useEffect(() => {
        if (formData.startTime) {
            const startDate = new Date(formData.startTime);
            if (
                !formData.endTime ||
                new Date(formData.endTime).getTime() < startDate.getTime()
            ) {
                setFormData({ ...formData, endTime: formData.startTime });
            }
        }
    }, [formData.startTime]);

    // ─── Helpers ────────────────────────────────────────────────────────

    const handleInputChange = (
        key: keyof typeof formData,
        value: any
    ) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    // MYS-66: hide the club selector when the org has a single club; the sole
    // club is auto-selected into formData.organizationLocation in the background.
    const { isSingleClub } = useSingleClubAutoSelect({
        selectedLocation: formData.organizationLocation,
        onSelect: (loc) => handleInputChange('organizationLocation', loc),
    });

    const toLocalDateTimeString = (utcDateString: string) => {
        const date = new Date(utcDateString);
        const timezoneOffset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - timezoneOffset * 60000);
        return localDate.toISOString().slice(0, 16);
    };

    const fromLocalDateTimeString = (
        dateTimeString: string | undefined
    ): Date | null => {
        if (!dateTimeString) return null;
        const date = new Date(dateTimeString);
        return isNaN(date.getTime()) ? null : date;
    };

    const toLocalDateTimeStringFromDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const toUTCString = (localDateTimeString: string) => {
        if (!localDateTimeString) return localDateTimeString;
        return new Date(localDateTimeString).toISOString();
    };

    const toEndOfDayUTC = (dateString: string) => {
        if (!dateString) return dateString;
        return new Date(dateString + 'T23:59:59').toISOString();
    };

    // ─── Recurrence gate ────────────────────────────────────────────────
    // Validated against the EXACT strings the create payload will carry, so
    // what this form accepts and what the API receives cannot diverge. Before
    // this, `isSubmitDisabled` was never passed to FormModal at all: every red
    // message in the recurrence block was decoration and Create stayed live,
    // which is how "recurring, no days selected" reached the server.
    const showRecurrence = !event && isRecurring;
    const recurrenceInput = {
        startTimeIso: toUTCString(formData.startTime),
        endDateIso: toEndOfDayUTC(formData.recurringEndDate || ''),
        daysOfWeek: formData.recurringDaysOfWeek ?? [],
    };
    const recurrenceError = showRecurrence
        ? validateRecurrence(recurrenceInput)
        : undefined;
    const recurrenceErrorMessage = recurrenceError
        ? translate(RECURRENCE_ERROR_KEYS[recurrenceError], {
              max:
                  recurrenceError === 'horizon_too_long'
                      ? MAX_RECURRING_HORIZON_DAYS
                      : MAX_RECURRING_OCCURRENCES,
          })
        : undefined;
    const dayError = DAY_ERROR_CODES.includes(
        recurrenceError as RecurrenceErrorCode
    );
    // The count is the only place the occurrence cap is ever stated to an
    // admin; without it the limit is discovered by being rejected by it.
    const occurrenceCount =
        showRecurrence && !recurrenceError
            ? countRecurringOccurrences(recurrenceInput)
            : 0;
    const occurrenceLabel =
        occurrenceCount === 1
            ? translate('event.recurring_occurrence_count_one')
            : translate('event.recurring_occurrence_count', {
                  count: occurrenceCount,
              });

    const closeModal = () => {
        modalOnClose();
        setLevelRequired(false);
        setRankingRequired(false);
        setIsRecurring(false);
        if (!event) {
            setFormData({ ...defaultValues });
        }
    };

    // ─── Submit ─────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        try {
            if (event) {
                const updateDto: UpdateEventInputDto = {
                    name: formData.name,
                    startTime: toUTCString(formData.startTime),
                    endTime: toUTCString(formData.endTime),
                    location: formData.location,
                    capacity: formData.capacity,
                    eventType: formData.eventType,
                    prizes: formData.prizes,
                    minLevel: formData.minLevel,
                    maxLevel: formData.maxLevel,
                    minRanking: formData.minRanking,
                    maxRanking: formData.maxRanking,
                    sportType: formData.sportType,
                    notificationTitle: formData.notificationTitle,
                    price: formData.price,
                    teamSize: formData.teamSize ?? null,
                    courtIds: formData.courtIds,
                    tournamentCategory: formData.tournamentCategory,
                    genderRestriction: formData.genderRestriction,
                    requiresLicenseNumber: formData.requiresLicenseNumber,
                    cohortId: formData.cohortId || null,
                    // null detaches the current coach — the field is optional
                    coachId: formData.coachId || null,
                };
                if (formData.organizationLocation?.id) {
                    updateDto.locationId = formData.organizationLocation.id;
                }
                await updateEvent({ id: event.id, dto: updateDto });
                toast({
                    title: translate('event.updated_successfully'),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                const { organizationLocation, ...rest } = formData;
                const createDto: CreateEventInputDto = {
                    ...rest,
                    startTime: toUTCString(formData.startTime),
                    endTime: toUTCString(formData.endTime),
                    locationId: organizationLocation?.id || '',
                    ...(formData.isRecurring && {
                        isRecurring: true,
                        recurringDaysOfWeek: formData.recurringDaysOfWeek,
                        recurringEndDate: toEndOfDayUTC(
                            formData.recurringEndDate || ''
                        ),
                    }),
                };
                await createEvent(createDto);
                toast({
                    title: formData.isRecurring
                        ? translate('event.recurring_created_successfully')
                        : translate('event.created_successfully'),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                onEventCreated?.();
            }
            closeModal();
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                translate('event.error_occurred');
            toast({
                title: event
                    ? translate('event.update_failed')
                    : translate('event.creation_failed'),
                description: errorMessage,
                status: 'error',
                duration: 10000,
                isClosable: true,
                position: 'top',
            });
        }
    };

    const showTriggerButton = !event && externalIsOpen === undefined;

    // Active staff, plus — when editing — the event's current coach even if
    // they have since been removed from the staff, so the field doesn't read
    // as "no coach" for an event that has one.
    const coachOptions = [
        { label: translate('event.none'), value: '' },
        ...(coachs?.map((c) => ({
            label: `${c.firstName} ${c.lastName}`,
            value: c.id,
        })) || []),
    ];
    if (event?.coach && !coachOptions.some((o) => o.value === event.coach?.id)) {
        coachOptions.push({
            label: `${event.coach.firstName} ${event.coach.lastName}`,
            value: event.coach.id,
        });
    }

    // ─── Render ─────────────────────────────────────────────────────────

    const labelColor = useColorModeValue('secondaryGray.900', 'white');
    const courtBorderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const courtBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const courtSelectedBg = useColorModeValue('brand.50', 'brand.900');
    const courtSelectedBorder = useColorModeValue('brand.400', 'brand.400');
    const dayBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const daySelectedBg = useColorModeValue('brand.500', 'brand.400');
    const dayColor = useColorModeValue('gray.600', 'gray.400');
    const daySelectedColor = useColorModeValue('white', 'white');

    return (
        <>
            {showTriggerButton && (
                <Button colorScheme="brand" onClick={onOpen}>
                    {translate('event.add_new')}
                </Button>
            )}
            <FormModal
                isOpen={modalIsOpen}
                onClose={closeModal}
                title={
                    event
                        ? translate('event.edit_event')
                        : translate('event.add_new')
                }
                onSubmit={handleSubmit}
                submitLabel={event ? translate('save') : translate('create')}
                cancelLabel={translate('cancel')}
                isSubmitting={isCreating || isUpdating}
                isSubmitDisabled={Boolean(recurrenceError)}
                size="2xl"
            >
                <FormStack>
                    {/* ═══════════════ ESSENTIALS ═══════════════ */}
                    <SectionHeader
                        icon={FiSettings}
                        label={translate('event.event_type')}
                    />
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
                        <FormField
                            label={`${translate('title')}*`}
                            value={formData.name}
                            onChange={(v: string) =>
                                handleInputChange('name', v)
                            }
                            error={
                                !formData.name
                                    ? translate('form.cannot_be_empty')
                                    : undefined
                            }
                        />
                        <FormField
                            type="select"
                            label={`${translate('event.sport')}*`}
                            value={formData.sportType}
                            onChange={(v) =>
                                handleInputChange('sportType', v)
                            }
                            options={buildSportOptions(formData.sportType)}
                        />
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap="16px">
                        <FormField
                            type="select"
                            label={`${translate('event.event_type')}*`}
                            value={formData.eventType}
                            onChange={(v) => {
                                const newType = v as EventType;
                                const updates: Partial<CreateEventFormType> = {
                                    eventType: newType,
                                };
                                if (newType !== EventType.COMPETITION) {
                                    updates.prizes = [];
                                }
                                if (newType !== EventType.OFFICIAL_COMPETITION) {
                                    updates.tournamentCategory = undefined;
                                }
                                // The licence gate only exists for tournaments;
                                // leaving it set on a class would silently block
                                // registrations from a control the form no
                                // longer shows.
                                if (
                                    newType !== EventType.COMPETITION &&
                                    newType !== EventType.OFFICIAL_COMPETITION
                                ) {
                                    updates.requiresLicenseNumber = false;
                                }
                                setFormData((prev) => ({ ...prev, ...updates }));
                            }}
                            options={EVENT_TYPE_OPTIONS.map((o) => ({
                                value: o.value,
                                label: translate(o.label as any),
                            }))}
                        />
                        <FormField
                            type="number"
                            label={`${translate('event.capacity')}*`}
                            value={formData.capacity}
                            onChange={(v) =>
                                handleInputChange('capacity', v || 0)
                            }
                            min={0}
                            error={
                                isNaN(Number(formData.capacity))
                                    ? translate('form.must_be_number')
                                    : undefined
                            }
                        />
                        <FormField
                            type="number"
                            label={translate('event.price')}
                            value={formData.price}
                            onChange={(v) => handleInputChange('price', v)}
                            min={0}
                            step={0.01}
                            helperText={translate('event.price_helper')}
                        />
                    </SimpleGrid>
                    <FormField
                        type="number"
                        label={translate('event.team_size')}
                        value={formData.teamSize ?? ''}
                        onChange={(v) =>
                            handleInputChange('teamSize', Number(v))
                        }
                        onEmpty={() => handleInputChange('teamSize', undefined)}
                        min={2}
                        step={1}
                        helperText={translate('event.team_size_helper')}
                    />

                    {/* Coach / animator running the event — optional */}
                    {coachsReadState === 'unreadable' ? (
                        // Same shape as the courts and cohorts arms above, and
                        // the same reason (R89-4 / D57): otherwise a failed read
                        // fell through to a picker holding only "None", which
                        // says this organization has no coaches.
                        <InlineErrorState
                            message={translate('coachs.error_loading')}
                        />
                    ) : coachsReadState === 'loading' ? (
                        // The same claim, briefly: a picker holding only "None"
                        // while the request is in flight says the same false
                        // thing a refused read said, for a shorter time.
                        // `common.loading` exists in both catalogues.
                        <Text color="gray.500" py={2}>
                            {translate('common.loading')}
                        </Text>
                    ) : coachsReadState === 'never-issued' ? (
                        // One control inside a form, so a text line rather than
                        // the `ScopePrompt` panel — run 114's call, kept by runs
                        // 116 and 118. `useListCoachs` is
                        // `enabled: Boolean(organizationId)`, and a refused or
                        // empty `GET my-organizations` leaves that undefined for
                        // the whole session (run 100). Borrowed key: the i18n
                        // catalogues are frozen inside open PR #577 (R96-7).
                        <Text color="gray.500" py={2}>
                            {translate(scopePromptI18nKey('organization') as any)}
                        </Text>
                    ) : (
                        <FormField
                            type="select"
                            label={translate('event.coach')}
                            value={formData.coachId || ''}
                            onChange={(v) =>
                                handleInputChange('coachId', v || undefined)
                            }
                            options={coachOptions}
                            helperText={translate('event.coach_helper')}
                        />
                    )}

                    {/* ═══════════════ LOCATION & COURTS ═══════════════ */}
                    <SectionHeader
                        icon={FiMapPin}
                        label={translate('event.club')}
                    />
                    {!isSingleClub && (
                        <FormControl isInvalid={!formData.organizationLocation}>
                            <FormLabel
                                {...typography.formLabel}
                                color={labelColor}
                                mb="6px"
                            >
                                {translate('event.club')}*
                            </FormLabel>
                            <LocationPicker
                                selectedLocation={formData.organizationLocation}
                                setSelectedLocation={(item) =>
                                    handleInputChange('organizationLocation', item)
                                }
                            />
                            <FormErrorMessage>
                                {translate('form.cannot_be_empty')}
                            </FormErrorMessage>
                        </FormControl>
                    )}
                    <FormControl>
                        <FormLabel
                            {...typography.formLabel}
                            color={labelColor}
                            mb="6px"
                        >
                            {translate('event.court')}
                        </FormLabel>
                        {courtsReadState === 'unreadable' ? (
                            // Otherwise a failed read fell through to the
                            // `event.no_courts_available` branch below, and the
                            // operator was told this venue has no courts while
                            // creating an event on it (R89-4 / D57).
                            <InlineErrorState
                                message={translate('courts.error_loading')}
                            />
                        ) : courtsReadState === 'loading' ? (
                            // The same false claim, briefly: "no courts
                            // available" while the request is still out says
                            // the same thing a refused read said, for a shorter
                            // time. `common.loading` is in both catalogues,
                            // which are frozen inside open PR #577 (R96-7).
                            <Text color="gray.500" py={2}>
                                {translate('common.loading')}
                            </Text>
                        ) : courts && courts.length > 0 ? (
                            <Wrap spacing={2}>
                                {courts.map((court) => {
                                    const isSelected =
                                        formData.courtIds?.includes(court.id);
                                    return (
                                        <WrapItem key={court.id}>
                                            <Box
                                                as="button"
                                                type="button"
                                                px={4}
                                                py={2}
                                                borderRadius="8px"
                                                border="1.5px solid"
                                                borderColor={
                                                    isSelected
                                                        ? courtSelectedBorder
                                                        : courtBorderColor
                                                }
                                                bg={
                                                    isSelected
                                                        ? courtSelectedBg
                                                        : courtBg
                                                }
                                                fontWeight={
                                                    isSelected ? '600' : '400'
                                                }
                                                fontSize="sm"
                                                cursor={
                                                    formData.organizationLocation
                                                        ? 'pointer'
                                                        : 'not-allowed'
                                                }
                                                opacity={
                                                    formData.organizationLocation
                                                        ? 1
                                                        : 0.4
                                                }
                                                transition="all 0.15s ease"
                                                _hover={
                                                    formData.organizationLocation
                                                        ? {
                                                              borderColor:
                                                                  courtSelectedBorder,
                                                          }
                                                        : {}
                                                }
                                                onClick={() => {
                                                    if (
                                                        !formData.organizationLocation
                                                    )
                                                        return;
                                                    const ids =
                                                        formData.courtIds || [];
                                                    handleInputChange(
                                                        'courtIds',
                                                        isSelected
                                                            ? ids.filter(
                                                                  (id) =>
                                                                      id !==
                                                                      court.id
                                                              )
                                                            : [...ids, court.id]
                                                    );
                                                }}
                                            >
                                                {court.name}
                                            </Box>
                                        </WrapItem>
                                    );
                                })}
                            </Wrap>
                        ) : (
                            <Text fontSize="sm" color="gray.500">
                                {courtsReadState === 'never-issued'
                                    ? translate('event.select_location_first')
                                    : translate('event.no_courts_available')}
                            </Text>
                        )}
                        <FormHelperText fontSize="xs" mt="4px">
                            {translate('event.courts_helper')}
                        </FormHelperText>
                    </FormControl>

                    {/* ═══════════════ SCHEDULE ═══════════════ */}
                    <SectionHeader
                        icon={FiCalendar}
                        label={translate('event.start_time')}
                    />
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
                        <DatePickerField
                            label={`${translate('event.start_time')}*`}
                            selected={fromLocalDateTimeString(
                                formData.startTime
                            )}
                            onChange={(date) =>
                                handleInputChange(
                                    'startTime',
                                    date
                                        ? toLocalDateTimeStringFromDate(date)
                                        : ''
                                )
                            }
                            isInvalid={!formData.startTime}
                            placeholder={translate(
                                'event.select_start_date_and_time'
                            )}
                        />
                        <DatePickerField
                            label={`${translate('event.end_time')}*`}
                            selected={fromLocalDateTimeString(
                                formData.endTime
                            )}
                            onChange={(date) =>
                                handleInputChange(
                                    'endTime',
                                    date
                                        ? toLocalDateTimeStringFromDate(date)
                                        : ''
                                )
                            }
                            minDate={
                                fromLocalDateTimeString(formData.startTime) ||
                                undefined
                            }
                            isInvalid={!formData.endTime}
                            placeholder={translate(
                                'event.select_end_date_and_time'
                            )}
                        />
                    </SimpleGrid>

                    {/* ── Recurring toggle ── */}
                    {!event && (
                        <Box>
                            <Checkbox
                                colorScheme="brandScheme"
                                isChecked={isRecurring}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setIsRecurring(checked);
                                    handleInputChange('isRecurring', checked);
                                    if (!checked) {
                                        handleInputChange(
                                            'recurringDaysOfWeek',
                                            []
                                        );
                                        handleInputChange(
                                            'recurringEndDate',
                                            ''
                                        );
                                    }
                                }}
                                fontWeight="500"
                                fontSize="sm"
                            >
                                <HStack spacing={2} align="center">
                                    <Icon as={FiRepeat} boxSize="14px" />
                                    <Text fontSize="sm">
                                        {translate(
                                            'event.create_recurring_events'
                                        )}
                                    </Text>
                                </HStack>
                            </Checkbox>
                            <Text fontSize="xs" color="gray.500" mt="4px" ml="24px">
                                {translate(
                                    'event.create_recurring_events_helper'
                                )}
                            </Text>
                        </Box>
                    )}

                    {/* ── Recurring details ── */}
                    <AnimatePresence>
                        {!event && isRecurring && (
                            <MotionBox
                                key="recurring-section"
                                variants={sectionReveal}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <VStack
                                    spacing={4}
                                    align="stretch"
                                    pl={6}
                                    borderLeft="2px solid"
                                    borderColor={courtSelectedBorder}
                                >
                                    <FormControl isInvalid={dayError}>
                                        <FormLabel
                                            {...typography.formLabel}
                                            color={labelColor}
                                            mb="8px"
                                        >
                                            {translate('event.days_of_week')}*
                                        </FormLabel>
                                        <HStack spacing={2} flexWrap="wrap">
                                            {DAYS_OF_WEEK.map((day) => {
                                                const isSelected =
                                                    formData.recurringDaysOfWeek?.includes(
                                                        day.value
                                                    );
                                                return (
                                                    <Box
                                                        key={day.value}
                                                        as="button"
                                                        type="button"
                                                        w="42px"
                                                        h="42px"
                                                        borderRadius="full"
                                                        bg={
                                                            isSelected
                                                                ? daySelectedBg
                                                                : dayBg
                                                        }
                                                        color={
                                                            isSelected
                                                                ? daySelectedColor
                                                                : dayColor
                                                        }
                                                        fontWeight="600"
                                                        fontSize="xs"
                                                        transition="all 0.15s ease"
                                                        _hover={{
                                                            transform:
                                                                'scale(1.08)',
                                                        }}
                                                        onClick={() => {
                                                            const current =
                                                                formData.recurringDaysOfWeek ||
                                                                [];
                                                            handleInputChange(
                                                                'recurringDaysOfWeek',
                                                                isSelected
                                                                    ? current.filter(
                                                                          (d) =>
                                                                              d !==
                                                                              day.value
                                                                      )
                                                                    : [
                                                                          ...current,
                                                                          day.value,
                                                                      ]
                                                            );
                                                        }}
                                                    >
                                                        {translate(
                                                            `event.days.${day.key}` as any
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </HStack>
                                        <FormHelperText fontSize="xs" mt="4px">
                                            {translate(
                                                'event.select_days_helper'
                                            )}
                                        </FormHelperText>
                                        <FormErrorMessage>
                                            {recurrenceErrorMessage}
                                        </FormErrorMessage>
                                    </FormControl>
                                    <FormField
                                        type="date"
                                        label={`${translate(
                                            'event.repeat_until'
                                        )}*`}
                                        value={formData.recurringEndDate || ''}
                                        onChange={(v) =>
                                            handleInputChange(
                                                'recurringEndDate',
                                                v
                                            )
                                        }
                                        // Day-specific reasons render under the
                                        // day picker above; everything else —
                                        // missing/invalid/too-far end date, a
                                        // range that yields nothing, an unset
                                        // start time — belongs here.
                                        error={
                                            recurrenceError && !dayError
                                                ? recurrenceErrorMessage
                                                : undefined
                                        }
                                        helperText={
                                            occurrenceCount > 0
                                                ? occurrenceLabel
                                                : translate(
                                                      'event.repeat_until_helper'
                                                  )
                                        }
                                    />
                                </VStack>
                            </MotionBox>
                        )}
                    </AnimatePresence>

                    {/* ═══════════════ COMPETITION SETTINGS ═══════════════ */}
                    <AnimatePresence>
                        {isCompetition && (
                            <MotionBox
                                key="competition-section"
                                variants={sectionReveal}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <SectionHeader
                                    icon={FiAward}
                                    label={translate(
                                        'event.tournament_category'
                                    )}
                                />
                                <VStack spacing={4} align="stretch">
                                    {isOfficialCompetition && (
                                        <FormField
                                            type="select"
                                            label={translate(
                                                'event.tournament_category'
                                            )}
                                            value={
                                                formData.tournamentCategory || ''
                                            }
                                            onChange={(v) =>
                                                handleInputChange(
                                                    'tournamentCategory',
                                                    v
                                                        ? (v as TournamentCategory)
                                                        : undefined
                                                )
                                            }
                                            options={[
                                                {
                                                    label: translate(
                                                        'event.select_tournament_category'
                                                    ),
                                                    value: '',
                                                },
                                                ...Object.values(
                                                    TournamentCategory
                                                ).map((c) => ({
                                                    label: c,
                                                    value: c,
                                                })),
                                            ]}
                                            helperText={translate(
                                                'event.tournament_category_helper'
                                            )}
                                        />
                                    )}
                                    <FormField
                                        type="select"
                                        label={translate(
                                            'event.gender_restriction'
                                        )}
                                        value={
                                            formData.genderRestriction ||
                                            GenderRestriction.NO_RESTRICTION
                                        }
                                        onChange={(v) =>
                                            handleInputChange(
                                                'genderRestriction',
                                                v
                                                    ? (v as GenderRestriction)
                                                    : undefined
                                            )
                                        }
                                        options={GENDER_OPTIONS.map((o) => ({
                                            value: o.value,
                                            label: translate(o.label as any),
                                        }))}
                                        helperText={translate(
                                            'event.gender_restriction_helper'
                                        )}
                                    />

                                    {/* Licence number required to register */}
                                    <FormControl>
                                        <Checkbox
                                            colorScheme="brandScheme"
                                            isChecked={
                                                !!formData.requiresLicenseNumber
                                            }
                                            onChange={(e) =>
                                                handleInputChange(
                                                    'requiresLicenseNumber',
                                                    e.target.checked
                                                )
                                            }
                                            fontWeight="500"
                                            fontSize="sm"
                                        >
                                            {translate(
                                                'event.requires_license_number'
                                            )}
                                        </Checkbox>
                                        <FormHelperText fontSize="xs" mt="4px">
                                            {translate(
                                                'event.requires_license_number_helper'
                                            )}
                                        </FormHelperText>
                                    </FormControl>

                                    {/* Prizes (competition only, not official) */}
                                    {formData.eventType ===
                                        EventType.COMPETITION && (
                                        <FormControl>
                                            <FormLabel
                                                {...typography.formLabel}
                                                color={labelColor}
                                                mb="6px"
                                            >
                                                {translate('event.prizes')}
                                            </FormLabel>
                                            <VStack
                                                spacing={3}
                                                align="stretch"
                                            >
                                                {formData.prizes?.map(
                                                    (prize, index) => (
                                                        <HStack
                                                            key={index}
                                                            spacing={3}
                                                        >
                                                            <Tag
                                                                size="lg"
                                                                colorScheme="brand"
                                                                variant="subtle"
                                                                borderRadius="full"
                                                                minW="44px"
                                                                justifyContent="center"
                                                            >
                                                                <TagLabel fontWeight="700">
                                                                    #
                                                                    {
                                                                        prize.place
                                                                    }
                                                                </TagLabel>
                                                            </Tag>
                                                            <FormField
                                                                label=""
                                                                value={
                                                                    formData
                                                                        .prizes[
                                                                        index
                                                                    ].reward ||
                                                                    ''
                                                                }
                                                                onChange={(v: string) => {
                                                                    const updated =
                                                                        [
                                                                            ...(formData.prizes ||
                                                                                []),
                                                                        ];
                                                                    updated[
                                                                        index
                                                                    ].reward = v;
                                                                    handleInputChange(
                                                                        'prizes',
                                                                        updated
                                                                    );
                                                                }}
                                                                placeholder={`${translate('event.prizes')} #${prize.place}`}
                                                            />
                                                            <TagCloseButton
                                                                onClick={() => {
                                                                    const updated =
                                                                        (
                                                                            formData.prizes ||
                                                                            []
                                                                        ).filter(
                                                                            (
                                                                                _,
                                                                                i
                                                                            ) =>
                                                                                i !==
                                                                                index
                                                                        );
                                                                    handleInputChange(
                                                                        'prizes',
                                                                        updated.map(
                                                                            (
                                                                                p,
                                                                                i
                                                                            ) => ({
                                                                                ...p,
                                                                                place:
                                                                                    i +
                                                                                    1,
                                                                            })
                                                                        )
                                                                    );
                                                                }}
                                                            />
                                                        </HStack>
                                                    )
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    colorScheme="brand"
                                                    onClick={() => {
                                                        const prizes =
                                                            formData.prizes ||
                                                            [];
                                                        handleInputChange(
                                                            'prizes',
                                                            [
                                                                ...prizes,
                                                                {
                                                                    amount: 0,
                                                                    place:
                                                                        prizes.length +
                                                                        1,
                                                                },
                                                            ]
                                                        );
                                                    }}
                                                    alignSelf="start"
                                                >
                                                    + {translate('add')}
                                                </Button>
                                            </VStack>
                                        </FormControl>
                                    )}
                                </VStack>
                            </MotionBox>
                        )}
                    </AnimatePresence>

                    {/* ═══════════════ RESTRICTIONS ═══════════════ */}
                    <SectionHeader
                        icon={FiUsers}
                        label={translate('event.level_required')}
                    />

                    {/* Level toggle + fields */}
                    <Checkbox
                        colorScheme="brandScheme"
                        isChecked={levelRequired}
                        onChange={(e) => {
                            setLevelRequired(e.target.checked);
                            if (!e.target.checked) {
                                handleInputChange('minLevel', undefined);
                                handleInputChange('maxLevel', undefined);
                            }
                        }}
                        fontWeight="500"
                        fontSize="sm"
                    >
                        {translate('event.level_required')}
                    </Checkbox>

                    <AnimatePresence>
                        {levelRequired && (
                            <MotionBox
                                key="level-section"
                                variants={sectionReveal}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <SimpleGrid
                                    columns={{ base: 1, md: 2 }}
                                    gap="16px"
                                >
                                    <FormField
                                        type="number"
                                        label={translate('event.min_level')}
                                        value={formData.minLevel ?? ''}
                                        onChange={(v) =>
                                            handleInputChange('minLevel', v)
                                        }
                                        min={MIN_LEVEL}
                                        max={MAX_LEVEL}
                                        error={
                                            formData.minLevel !== undefined &&
                                            (isNaN(Number(formData.minLevel)) ||
                                                Number(formData.minLevel) <
                                                    MIN_LEVEL ||
                                                Number(formData.minLevel) >
                                                    MAX_LEVEL)
                                                ? translate(
                                                      'user.user_level_invalid'
                                                  )
                                                : undefined
                                        }
                                    />
                                    <FormField
                                        type="number"
                                        label={translate('event.max_level')}
                                        value={formData.maxLevel ?? ''}
                                        onChange={(v) =>
                                            handleInputChange('maxLevel', v)
                                        }
                                        min={MIN_LEVEL}
                                        max={MAX_LEVEL}
                                        error={
                                            formData.maxLevel !== undefined &&
                                            (isNaN(Number(formData.maxLevel)) ||
                                                Number(formData.maxLevel) <
                                                    MIN_LEVEL ||
                                                Number(formData.maxLevel) >
                                                    MAX_LEVEL)
                                                ? translate(
                                                      'user.user_level_invalid'
                                                  )
                                                : undefined
                                        }
                                    />
                                </SimpleGrid>
                            </MotionBox>
                        )}
                    </AnimatePresence>

                    {/* Ranking toggle + fields (official competition only) */}
                    <AnimatePresence>
                        {isOfficialCompetition && (
                            <MotionBox
                                key="ranking-section"
                                variants={sectionReveal}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <VStack spacing={4} align="stretch">
                                    <Checkbox
                                        colorScheme="brandScheme"
                                        isChecked={rankingRequired}
                                        onChange={(e) => {
                                            setRankingRequired(
                                                e.target.checked
                                            );
                                            if (!e.target.checked) {
                                                handleInputChange(
                                                    'minRanking',
                                                    undefined
                                                );
                                                handleInputChange(
                                                    'maxRanking',
                                                    undefined
                                                );
                                            }
                                        }}
                                        fontWeight="500"
                                        fontSize="sm"
                                    >
                                        {translate('event.ranking_required')}
                                    </Checkbox>

                                    <AnimatePresence>
                                        {rankingRequired && (
                                            <MotionBox
                                                key="ranking-fields"
                                                variants={sectionReveal}
                                                initial="hidden"
                                                animate="visible"
                                                exit="exit"
                                            >
                                                <SimpleGrid
                                                    columns={{
                                                        base: 1,
                                                        md: 2,
                                                    }}
                                                    gap="16px"
                                                >
                                                    <FormField
                                                        type="select"
                                                        label={translate(
                                                            'event.min_ranking'
                                                        )}
                                                        value={
                                                            formData.minRanking ||
                                                            ''
                                                        }
                                                        onChange={(v) =>
                                                            handleInputChange(
                                                                'minRanking',
                                                                v === ''
                                                                    ? undefined
                                                                    : v
                                                            )
                                                        }
                                                        options={[
                                                            {
                                                                label: translate(
                                                                    'event.none'
                                                                ),
                                                                value: '',
                                                            },
                                                            ...RANKING_OPTIONS,
                                                        ]}
                                                        helperText={translate(
                                                            'event.min_ranking_helper'
                                                        )}
                                                    />
                                                    <FormField
                                                        type="select"
                                                        label={translate(
                                                            'event.max_ranking'
                                                        )}
                                                        value={
                                                            formData.maxRanking ||
                                                            ''
                                                        }
                                                        onChange={(v) =>
                                                            handleInputChange(
                                                                'maxRanking',
                                                                v === ''
                                                                    ? undefined
                                                                    : v
                                                            )
                                                        }
                                                        options={[
                                                            {
                                                                label: translate(
                                                                    'event.none'
                                                                ),
                                                                value: '',
                                                            },
                                                            ...RANKING_OPTIONS,
                                                        ]}
                                                        helperText={translate(
                                                            'event.max_ranking_helper'
                                                        )}
                                                    />
                                                </SimpleGrid>
                                            </MotionBox>
                                        )}
                                    </AnimatePresence>
                                </VStack>
                            </MotionBox>
                        )}
                    </AnimatePresence>

                    {/* Cohort restriction */}
                    {cohortsReadState === 'unreadable' ? (
                        <InlineErrorState
                            message={translate('cohort.error_loading')}
                        />
                    ) : cohortsReadState === 'loading' ? (
                        <Text color="gray.500" py={2}>
                            {translate('common.loading')}
                        </Text>
                    ) : cohortsReadState === 'never-issued' ? (
                        // `enabled: Boolean(organizationId)`, and a refused or
                        // empty `GET my-organizations` leaves that undefined for
                        // the whole session (run 100). Without this arm the
                        // select renders holding only "None", which on the EDIT
                        // path shows a cohort-restricted event as open to
                        // everyone. Borrowed key, per the i18n freeze.
                        <Text color="gray.500" py={2}>
                            {translate(scopePromptI18nKey('organization') as any)}
                        </Text>
                    ) : (
                        <FormField
                            type="select"
                            label={translate('cohort.cohort')}
                            value={formData.cohortId || ''}
                            onChange={(v) =>
                                handleInputChange('cohortId', v || undefined)
                            }
                            options={[
                                {
                                    label: translate('event.none'),
                                    value: '',
                                },
                                ...(cohorts
                                    ?.filter((c) => !c.systemType)
                                    .map((c) => ({
                                        label: `${c.name} (${c.memberCount} ${translate('cohort.members_count')})`,
                                        value: c.id,
                                    })) || []),
                            ]}
                            helperText={translate(
                                'event.cohort_restriction_helper'
                            )}
                        />
                    )}

                    {/* ═══════════════ NOTIFICATION ═══════════════ */}
                    <SectionHeader
                        icon={FiBell}
                        label={translate('event.notification_title')}
                    />
                    <FormField
                        label={translate('event.notification_title')}
                        value={formData.notificationTitle}
                        onChange={(v: string) =>
                            handleInputChange('notificationTitle', v)
                        }
                        helperText={translate(
                            'event.notification_title_description'
                        )}
                    />
                </FormStack>
            </FormModal>
        </>
    );
}
